import { Styles, css } from '@styled-system/css';
import { stack } from '@styled-system/patterns';

import { Button } from '@/shared/components/ui/Button';
import { Separator } from '@/shared/components/ui/Separator';

interface SubscriptionPlanCardProps {
  planName: string;
  monthlyPrice?: number;
  baseMonthlyPrice?: number;
  freeTrialDays?: number;
  isHighlighted?: boolean;
  subscriptionType: 'subscribe' | 'contact';
  features: Array<{ label: string; value: string | number; isQuota?: boolean }>;
  isCurrentPlan?: boolean;
  isDowngrade?: boolean;
  handleSubscribe?: () => Promise<void>;
  handleContact?: () => void;
  handleCancel?: () => void;
}

export const SubscriptionPlanCard = ({
  planName,
  monthlyPrice,
  baseMonthlyPrice,
  freeTrialDays = 0,
  isHighlighted = false,
  subscriptionType,
  features,
  isCurrentPlan = false,
  isDowngrade = false,
  handleSubscribe,
  handleContact,
  handleCancel,
}: SubscriptionPlanCardProps) => {
  const cardStyles = stack({
    position: 'relative',
    direction: 'column',
    gap: 0,
    width: '20rem',
    height: '30rem',
    backgroundColor: 'base.segBg',
    borderRadius: 'lg',
    border: '0.75px solid',
    borderColor: 'base.border',
    overflow: 'hidden',
    fontFamily: 'Manrope, sans-serif',
  });

  const cardHighlightedStyles = css({
    '&::before': {
      content: '""',
      position: 'absolute',
      transform: 'translate(-0.28rem, 0.19rem)',
      zIndex: -1,
      width: '19rem',
      height: '30rem',
      borderRadius: 'lg',
      bg: 'accent.primary',
      display: 'block',
    },
  });

  const titleContainerStyles = stack({
    direction: 'column',
    gap: 'sm',
    pl: '1.75rem',
    pt: '1.69rem',
    pb: '1.44rem',
  });

  const planNameStyles = css({
    fontSize: 'md',
    fontWeight: 'medium',
    color: 'font.lowkey',
    lineHeight: '1.07',
  });

  const priceContainerStyles = stack({
    direction: 'row',
    gap: 'xs',
    alignItems: 'end',
  });

  const basePriceStyles = css({
    fontSize: 'md',
    fontWeight: 'medium',
    color: 'font.lowkey',
    textDecoration: 'line-through',
    lineHeight: '1.07',
  });

  const priceRowStyles = stack({
    direction: 'row',
    alignItems: 'end',
    gap: 'xxs',
  });

  const priceStyles = css({
    fontSize: '2xl',
    fontWeight: 'bold',
    color: 'font.primary',
    lineHeight: '1.07',
  });

  const periodStyles = css({
    fontSize: 'lg',
    fontWeight: 'medium',
    color: 'font.secondary',
    lineHeight: '1.07',
  });

  const freeTrialPriceContainerStyles = stack({
    direction: 'column',
    gap: 'xs',
    alignItems: 'start',
  });

  const freeTrialTextStyles = css({
    fontSize: '2xl',
    fontWeight: 'bold',
    color: 'font.primary',
    lineHeight: '1.07',
    whiteSpace: 'nowrap',
  });

  const freeTrialNoticeStyles = css({
    fontSize: 'xs',
    fontWeight: 'medium',
    color: 'accent.primary',
    lineHeight: '1.07',
  });

  const separatorLineStyles = css({
    width: '16.1875rem',
    alignSelf: 'center',
  });

  const featuresContainerStyles = stack({
    direction: 'column',
    gap: 'md',
    pl: '1.44rem',
    pr: '1.37rem',
    py: '1.25rem',
  });

  const featuresListStyles = stack({
    direction: 'column',
    gap: 'md',
  });

  const featureRowStyles = stack({
    direction: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  });

  const featureLabelStyles = css({
    fontSize: 'sm',
    fontWeight: 'medium',
    color: 'font.lowkey',
    lineHeight: '1.07',
    whiteSpace: 'nowrap',
  });

  const featureValueStyles = css({
    fontSize: 'sm',
    fontWeight: 'bold',
    color: 'font.primary',
    lineHeight: '1.07',
  });

  const contactDescriptionStyles = css({
    fontSize: 'sm',
    fontWeight: 'medium',
    color: 'font.lowkey',
    lineHeight: '1.07',
  });

  const cancelButtonStyles = css({
    position: 'absolute',
    bottom: '0.75rem',
    alignSelf: 'center',
    fontSize: 'sm',
    fontWeight: 'medium',
    color: 'font.tertiary',
    bg: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textDecoration: 'underline',
    transition: 'color 0.2s',
    '&:hover': {
      color: 'font.secondary',
    },
  });

  const buttonSpaceStyles = css({
    flex: 1,
  });

  const getButtonStyles = () => {
    const baseStyles: Styles = {
      height: '2.125rem',
      width: '15.1875rem',
      alignSelf: 'center',
      mb: '2.38rem',
      borderRadius: 'md',
      padding: '0',
      lineHeight: '1.07',
    };

    if (isCurrentPlan) {
      return css(baseStyles, {
        border: '0.5px solid',
        borderColor: 'base.border',
        backgroundColor: 'transparent',
        cursor: 'not-allowed',
      });
    }

    if (isDowngrade) {
      return css(baseStyles, {
        border: '0.5px solid',
        borderColor: 'base.border',
        backgroundColor: 'base.segBg',
        cursor: 'not-allowed',
        opacity: '0.6',
      });
    }

    if (subscriptionType === 'contact') {
      return css(baseStyles, {
        backgroundColor: 'base.border !important',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'base.selectedBg !important',
        },
      });
    } else {
      return css(baseStyles, {
        backgroundColor: 'accent.primary !important',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'accent.primaryHover !important',
        },
      });
    }
  };

  const handleClick = async () => {
    if (subscriptionType === 'contact') {
      handleContact?.();
      return;
    }

    if (isCurrentPlan || isDowngrade || !handleSubscribe) return;

    try {
      await handleSubscribe();
    } catch (error) {
      console.error('Subscription error:', error);
    }
  };

  const getButtonText = () => {
    if (isCurrentPlan) return 'Votre plan actuel';
    if (isDowngrade) return 'Votre plan actuel (facturé annuellement)';
    if (subscriptionType === 'contact') return 'Nous Contacter';
    if (freeTrialDays > 0) return `Commencer ${freeTrialDays} jours gratuits`;
    return 'Commencer maintenant';
  };

  return (
    <div className={`${cardStyles} ${isHighlighted ? cardHighlightedStyles : ''}`}>
      {/* Highlighted overlay pour le plan recommandé */}
      {/* {isHighlighted && <div className={highlightedOverlay} />} */}

      {/* Title container */}
      <div className={titleContainerStyles}>
        {/* Plan name */}
        <div className={planNameStyles}>{planName}</div>

        {/* Price section */}
        <div className={priceContainerStyles}>
          {baseMonthlyPrice && <div className={basePriceStyles}>{baseMonthlyPrice.toFixed(2)} €</div>}

          {freeTrialDays > 0 && monthlyPrice ? (
            <div className={freeTrialPriceContainerStyles}>
              <div className={priceRowStyles}>
                <span className={priceStyles}>{monthlyPrice.toFixed(2)} €</span>
                <span className={periodStyles}>/mois</span>
              </div>
              <span className={freeTrialTextStyles}>Avec {freeTrialDays}j GRATUITS</span>
            </div>
          ) : monthlyPrice ? (
            <div className={priceRowStyles}>
              <span className={priceStyles}>{monthlyPrice.toFixed(2)} €</span>
              <span className={periodStyles}>/mois</span>
            </div>
          ) : (
            <div className={priceRowStyles}>
              <span className={priceStyles}>Custom</span>
              <span className={periodStyles}>/mois</span>
            </div>
          )}
        </div>
      </div>

      {/* Separator line */}
      <Separator className={separatorLineStyles} />

      {/* Features container */}
      <div className={featuresContainerStyles}>
        {/* Features list */}
        <div className={featuresListStyles}>
          {features.map((feature, index) => (
            <div key={index} className={featureRowStyles}>
              <span className={featureLabelStyles}>{feature.label}</span>
              <span className={featureValueStyles}>{feature.value}</span>
            </div>
          ))}
        </div>

        {/* Contact description pour le plan Expert */}
        {subscriptionType === 'contact' && (
          <div className={contactDescriptionStyles}>
            Avez-vous besoin d'analyses plus approfondies ou personnalisées ?
          </div>
        )}

        {/* Free trial notice */}
        {freeTrialDays > 0 && subscriptionType !== 'contact' && (
          <div className={freeTrialNoticeStyles}>
            Essai GRATUIT de {freeTrialDays} jours disponible, annulable à tout moment !
          </div>
        )}
      </div>

      {/* CTA Button */}
      <div className={buttonSpaceStyles} />
      <Button
        handleClick={handleClick}
        showLoader
        disabled={isCurrentPlan || isDowngrade}
        className={getButtonStyles()}
      >
        {getButtonText()}
      </Button>

      {/* Cancel Subscription Button */}
      {isCurrentPlan && (
        <button className={cancelButtonStyles} onClick={handleCancel}>
          Annuler mon abonnement
        </button>
      )}
    </div>
  );
};
